import React, { useCallback, useMemo, useState } from "react";

import { Empty, Pagination, Table, message } from "antd";

import Swal from "sweetalert2";

import LdasApi from "../../../../apis/Ldas.api";
import { LDAS_DELETE_SWAL_CONFIG } from "../LdasUtils";

import LdasReportAddModal from "./LdasReportAddModal";

const getParamColumns = () => [
  {
    title: "파라미터",
    dataIndex: "parameterName",
    key: "parameterName",
    render: (value) => <span className="ldas-report-param">{value}</span>,
  },
  {
    title: "기준",
    dataIndex: "standardValue",
    key: "standardValue",
    render: (value) => <span className="ldas-cell-text">{value}</span>,
  },
  {
    title: "측정값",
    dataIndex: "measuredValue",
    key: "measuredValue",
    render: (value) => <span className="ldas-report-value">{value}</span>,
  },
];

const TABLE_COMPONENTS = {
  header: {
    cell: ({ children, className, style, ...rest }) => (
      <th
        {...rest}
        className={["ldas-table-header-cell", className]
          .filter(Boolean)
          .join(" ")}
        style={style}
      >
        {children}
      </th>
    ),
  },
};

const LdasLotDetailReportSection = ({
  reports,
  reportCount,
  lotId,
  onRefetch,
  pagination,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [pendingReports, setPendingReports] = useState([]);

  const handleModalClose = useCallback(() => {
    setEditingReport(null);
    setModalOpen(false);
  }, []);

  const handleReportEdit = useCallback((report) => {
    setEditingReport(report);
    setModalOpen(true);
  }, []);

  // 리포트 등록 (낙관적 업데이트)
  const handleReportCreate = useCallback(
    async (reportData) => {
      const tempId = `temp-${Date.now()}`;
      const optimistic = {
        id: tempId,
        testBody: reportData.testBody,
        testDate: reportData.testDate,
        batchNumber: reportData.batchNumber,
        result: null,
        parameters: reportData.parameters.map((p, i) => ({
          ...p,
          id: `temp-p-${i}`,
        })),
        _pending: true,
      };

      handleModalClose();
      setPendingReports((prev) => [optimistic, ...prev]);

      try {
        const param = {
          lotId: Number(lotId),
          testBody: reportData.testBody,
          testDate: reportData.testDate,
          batchNumber: reportData.batchNumber,
          parameters: reportData.parameters,
        };
        // console.log("createLdasTestReport param", param);
        const res = await LdasApi.createLdasTestReport(param);
        // console.log("createLdasTestReport res", res);
        if (res?.code !== 200) {
          message.error(res?.message ?? "리포트를 추가할 수 없습니다");
          setPendingReports((prev) => prev.filter((r) => r.id !== tempId));
          return;
        }
        await onRefetch();
        setPendingReports((prev) => prev.filter((r) => r.id !== tempId));
      } catch (e) {
        console.error("createLdasTestReport", e);
        message.error("리포트를 추가할 수 없습니다");
        setPendingReports((prev) => prev.filter((r) => r.id !== tempId));
      }
    },
    [lotId, onRefetch, handleModalClose],
  );

  // 리포트 수정
  const handleReportUpdate = useCallback(
    async (reportData) => {
      try {
        const param = {
          testBody: reportData.testBody,
          testDate: reportData.testDate,
          batchNumber: reportData.batchNumber,
          result: "PASS",
          parameters: reportData.parameters,
        };
        console.log("updateLdasTestReport param", param);
        const res = await LdasApi.updateLdasTestReport(
          Number(reportData.id),
          param,
        );
        console.log("updateLdasTestReport res", res);
        if (res?.code !== 200) {
          message.error(res?.message ?? "리포트를 수정할 수 없습니다");
          return;
        }
        handleModalClose();
        await onRefetch();
      } catch (e) {
        console.error("updateLdasTestReport", e);
        message.error("리포트를 수정할 수 없습니다");
      }
    },
    [onRefetch, handleModalClose],
  );

  // 리포트 삭제
  const handleReportDelete = useCallback(
    (report) => {
      Swal.fire({
        ...LDAS_DELETE_SWAL_CONFIG,
        title: `"${report.testBody}" 리포트를 삭제할까요?`,
        text: "정말로 이 리포트를 삭제하시겠습니까?",
      }).then(async (result) => {
        if (!result.isConfirmed) return;
        try {
          const res = await LdasApi.deleteLdasTestReport(report.id);
          console.log("deleteLdasTestReport res", res);
          if (res?.code !== 200) {
            message.error(res?.message ?? "리포트를 삭제할 수 없습니다");
            return;
          }
          await onRefetch();
        } catch (e) {
          console.error("deleteLdasTestReport", e);
          message.error("리포트를 삭제할 수 없습니다");
        }
      });
    },
    [onRefetch],
  );

  const paramColumns = useMemo(() => getParamColumns(), []);

  return (
    <>
      <div className="ldas-detail-section-card">
        <div className="ldas-detail-section-card__head">
          <div className="ldas-detail-section-card__title-wrap">
            <span className="ldas-detail-section-card__title">
              🧪 테스트 리포트
            </span>
            <span className="ldas-count-badge ldas-count-badge--active">
              {reportCount ?? reports.length}
            </span>
          </div>
          <button
            type="button"
            className="ldas-detail-lot-add-btn"
            onClick={() => {
              setEditingReport(null);
              setModalOpen(true);
            }}
          >
            + 리포트 추가
          </button>
        </div>

        <div className="ldas-detail-report-list">
          {pendingReports.length === 0 && reports?.length === 0 && (
            <div className="ldas-table-card ldas-table-card--borderless">
              <Table
                columns={paramColumns}
                dataSource={[]}
                rowKey="id"
                pagination={false}
                size="small"
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="등록된 테스트 리포트가 없습니다"
                    />
                  ),
                }}
                components={TABLE_COMPONENTS}
              />
            </div>
          )}
          {pendingReports.map((report) => (
            <div
              key={report.id}
              className="ldas-report-item"
              style={{ opacity: 0.5, pointerEvents: "none" }}
            >
              <div className="ldas-report-item__header">
                <div className="ldas-report-item__info">
                  <div className="ldas-report-item__title-row">
                    <span className="ldas-report-item__lab">
                      {report.testBody}
                    </span>
                    <span className="ldas-report-item__status">
                      ⏳ 저장 중...
                    </span>
                  </div>
                  <p className="ldas-report-item__meta">
                    {report.testDate} · {report.batchNumber}
                  </p>
                </div>
              </div>

              <div className="ldas-table-card ldas-table-card--borderless">
                <Table
                  columns={paramColumns}
                  dataSource={report.parameters}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  components={TABLE_COMPONENTS}
                />
              </div>
            </div>
          ))}

          {reports?.map((report) => (
            <div key={report.id} className="ldas-report-item">
              <div className="ldas-report-item__header">
                <div className="ldas-report-item__info">
                  <div className="ldas-report-item__title-row">
                    <span className="ldas-report-item__lab">
                      {report.testBody}
                    </span>
                    <span className="ldas-report-item__status ldas-report-item__status--passed">
                      ✅ {report.result}
                    </span>
                  </div>
                  <p className="ldas-report-item__meta">
                    {report.testDate} · {report.batchNumber}
                  </p>
                </div>
                <div className="ldas-actions-cell">
                  <button
                    type="button"
                    className="ldas-action-btn"
                    onClick={() => handleReportEdit(report)}
                  >
                    수정
                  </button>
                  <button
                    type="button"
                    className="ldas-action-btn ldas-action-btn--danger"
                    onClick={() => handleReportDelete(report)}
                  >
                    삭제
                  </button>
                </div>
              </div>

              <div className="ldas-table-card ldas-table-card--borderless">
                <Table
                  columns={paramColumns}
                  dataSource={report.parameters}
                  rowKey="id"
                  pagination={false}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="등록된 파라미터가 없습니다"
                      />
                    ),
                  }}
                  size="small"
                  components={TABLE_COMPONENTS}
                />
              </div>
            </div>
          ))}
        </div>

        {pagination && (
          <div
            className="ldas-table-card ldas-table-card--borderless"
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Pagination size="small" {...pagination} />
          </div>
        )}
      </div>

      <LdasReportAddModal
        visible={modalOpen}
        editingReport={editingReport}
        onCancel={handleModalClose}
        onSave={editingReport ? handleReportUpdate : handleReportCreate}
      />
    </>
  );
};

export default LdasLotDetailReportSection;
